import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import axios from 'axios';

const CoinList = () => {
    const [coinList, setCoinList] = useState([]);
    const [category, setCategory] = useState('binance-smart-chain');

    useEffect(() => {
        axios
            .get(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&category=${category}&order=market_cap_desc&per_page=200&sparkline=true&price_change_percentage=1h,24h,7d,14d,30d,200d,1y`)
            .then(res => setCoinList(res.data.filter(coin => !!coin.current_price)))
            .catch(err => console.log(err))
    }, [category])

    function getWindowDimensions() {
        const { innerWidth: width, innerHeight: height } = window;
        return {
          width,
          height
        };
    }
      
    function useWindowDimensions() {
        const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
        
        useEffect(() => {
            function handleResize() {
            setWindowDimensions(getWindowDimensions());
            }
        
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);
        
        return windowDimensions;
    }

    let { width } = useWindowDimensions();
    if (width < 1200) {
        width = 1200;
    }
    const numColumns = 14;

    const columns = [
        { field: 'name', headerName: 'Name', renderCell: (cellValues) => {
            return <Tooltip title={cellValues.row.name}>
                <span>{cellValues.row.name}</span>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'symbol', headerName: 'Symbol', renderCell: (cellValues) => {
            return <Tooltip title={cellValues.row.symbol}>
                <a className="text-blue-300" href={`https://www.coingecko.com/en/coins/${cellValues.row.id}`} target="_blank" rel="noreferrer">{cellValues.row.symbol}</a>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'logo', headerName: 'Logo', renderCell: (cellValues) => {
            return <img src={cellValues.row.image} width="50" alt={cellValues.row.name + " logo"} />
        }, width: width/numColumns},
        { field: 'current_price', headerName: 'Price (USD)', renderCell: (cellValues) => {
            return <Tooltip title={formatPrice(cellValues.row.current_price)}>
                <span>{formatPrice(cellValues.row.current_price)}</span>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'atl', headerName: 'ATL', renderCell: (cellValues) => {
            return <Tooltip title={formatPrice(cellValues.row.atl)}>
                <span>{formatPrice(cellValues.row.atl)}</span>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'atl_change_percentage', headerName: '% Off ATL', renderCell: (cellValues) => {
            return <Tooltip title={addCommas(cellValues.row.atl_change_percentage)}>
                <span>{addCommas(cellValues.row.atl_change_percentage)}</span>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'ath', headerName: 'ATH', renderCell: (cellValues) => {
            return <Tooltip title={formatPrice(cellValues.row.ath)}>
                <span>{formatPrice(cellValues.row.ath)}</span>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'ath_change_percentage', headerName: '% Off ATH', renderCell: (cellValues) => {
            return <Tooltip title={addCommas(cellValues.row.ath_change_percentage)}>
                <span>{addCommas(cellValues.row.ath_change_percentage)}</span>
            </Tooltip>
        }, width: width/numColumns},
        { field: 'price_change_percentage_1h_in_currency', headerName: '1H %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_1h_in_currency
            return <Tooltip title={addCommas(change)}>
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
        { field: 'price_change_percentage_24h_in_currency', headerName: '24H %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_24h_in_currency
            return <Tooltip title={addCommas(change)}>
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
        { field: 'price_change_percentage_7d_in_currency', headerName: '7D %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_7d_in_currency
            return <Tooltip title={addCommas(change)}>
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
        { field: 'price_change_percentage_14d_in_currency', headerName: '14D %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_14d_in_currency
            return <Tooltip title={addCommas(change)}> 
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
        { field: 'price_change_percentage_30d_in_currency', headerName: '30D %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_30d_in_currency
            return <Tooltip title={addCommas(change)}> 
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
        { field: 'price_change_percentage_200d_in_currency', headerName: '200D %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_200d_in_currency
            return <Tooltip title={addCommas(change)}>
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
        { field: 'price_change_percentage_1y_in_currency', headerName: '1Y %', renderCell: (cellValues) => {
            const change = cellValues.row.price_change_percentage_1y_in_currency
            return <Tooltip title={addCommas(change)}>
                <span style={{ color: change < 0 ? "red" : change === 0 ? "inherit" : "green"}}>{addCommas(change)}</span>
            </Tooltip> 
        }, width: width/numColumns},
    ]

    function formatPrice(value) {
        if (value === null || value === undefined || isNaN(value)) return '';
        if (value === 0) return '0.00';
        const abs = Math.abs(value);
        if (abs < 0.01) {
            return Number(value).toExponential(2);
        }
        return addCommas(value);
    }

    const addCommas = (number) => {
        if (number) {
            const string = `${number?.toFixed(2)}`;
            const split = string.split(".");
            const bigNum = split[0];
            const decimal = split[1];
            const digitArr = bigNum.split("");
            const output = ["."];
            let counter = -1;
            for (let i = digitArr.length; i >= 0; i--) {
                output.push(digitArr[i]);
                counter++;
                if (counter === 3 && i > 0 && digitArr[0] !== "-") {
                    output.push(",");
                    counter = 0;
                }
            }
            
            return output.reverse().concat(decimal?.split("")).join("");
        }
    }

    return (
        <div>
            <h1>{category.split("-")[0].slice(0,1).toUpperCase()}{category.split("-")[0].slice(1)} Coins</h1>
            <button onClick={() => setCategory('binance-smart-chain')} style={category !== "binance-smart-chain" ? {color: "blue", textDecoration: "underline"} : {}}>Binance</button>
            <button onClick={() => setCategory('avalanche-ecosystem')} style={category !== "avalanche-ecosystem" ? {color: "blue", textDecoration: "underline"} : {}}>Avalanche</button>
            <button onClick={() => setCategory('solana-meme-coins')} style={category !== "solana-meme-coins" ? {color: "blue", textDecoration: "underline"} : {}}>Solana</button>
            <div style={{ height: '90vh', width: '100%' }}>
                <DataGrid
                    rows={coinList}
                    columns={columns}
                    
                />
            </div>
        </div>
    )
}

export default CoinList;